/**
 * Dental chart lines for the prescription, used by the offline PDF.
 *
 * Mirrors chart_for_print() in kaagazy-backend/app/modules/visit/dental.py so
 * a prescription printed offline reads the same as one printed by the server.
 */
import { CONDITION_BY_KEY } from "../constants/dental.constants";
import type { DentalChart } from "../schemas/dental.schema";

const QUADRANT = ["", "Upper right", "Upper left", "Lower left", "Lower right"];
const ROMAN = ["", "I", "II", "III"];

export interface DentalPrint {
  findings: string[];
  plan: { where: string; label: string; sittings: number }[];
}

export function chartForPrint(chart: DentalChart | null | undefined): DentalPrint {
  if (!chart) return { findings: [], plan: [] };

  const findings = [...chart.findings]
    .sort((a, b) => a.tooth - b.tooth)
    .map((f) => {
      const parts: string[] = f.conditions.map((c) => CONDITION_BY_KEY[c]?.label ?? c);
      if (f.mobility_grade) parts.push(`Mobility grade ${ROMAN[f.mobility_grade]}`);
      return parts.length ? `Tooth ${f.tooth}: ${parts.join(", ")}` : null;
    })
    .filter((s): s is string => s !== null);

  const plan = chart.plan_items
    .filter((i) => i.status !== "cancelled")
    .map((i) => ({
      where: i.tooth !== null ? `Tooth ${i.tooth}` : i.quadrant !== null ? `${QUADRANT[i.quadrant]} quadrant` : "Full mouth",
      label: i.label,
      sittings: i.sittings_planned,
    }));

  return { findings, plan };
}
